import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import type { StripeConfigContextType } from './types';

const StripeConfigContext = createContext<StripeConfigContextType | null>(null);

interface StripeProviderProps {
  children: React.ReactNode;
  publishableKey: string;
}

let stripePromise: Promise<Stripe | null> | null = null;

export function StripeProvider({
  children,
  publishableKey,
}: StripeProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!publishableKey) {
      setError(new Error('Stripe publishable key is required'));
      setIsLoading(false);
      return;
    }

    if (!stripePromise) {
      stripePromise = loadStripe(publishableKey);
    }

    stripePromise
      .then(() => {
        setIsLoading(false);
      })
      .catch((err) => {
        setError(err);
        setIsLoading(false);
      });
  }, [publishableKey]);

  const value: StripeConfigContextType = {
    publishableKey,
    isLoading,
    error,
  };

  return (
    <StripeConfigContext.Provider value={value}>
      {children}
    </StripeConfigContext.Provider>
  );
}

export function useStripeConfig(): StripeConfigContextType {
  const context = useContext(StripeConfigContext);
  if (!context) {
    throw new Error('useStripeConfig must be used within a StripeProvider');
  }
  return context;
}

export async function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    throw new Error(
      'Stripe has not been initialized. Make sure to wrap your app with StripeProvider.'
    );
  }
  return stripePromise;
}
